import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Alert,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap'

import { Ghost } from '../../../../../components/Ghost'
import { LoaderWMS } from '../../components/LoaderWMS'

import WmsInventarioOSService from '../../../../../services/database/WmsInventarioOSService'

import { sendErrorApiMessage } from '../../../../../utils/sendErrorApiMessage'

import { ArrowsClockwise, Eye, XCircle } from 'phosphor-react'
import {
  Check2Square,
  Hourglass,
  HourglassBottom,
  HourglassSplit,
  PersonFillAdd,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'
import { CustomButton } from '../../../../../components/CustomButton'
import { useOrdenation } from '../../../../../hooks/useOrdenation'
import { getValue } from '../../../../../utils/getValue'
import { ModalFuncionarios } from '../../../../ModalFuncionarios'
import { ComponentContainer, ComponentContent } from '../../styles'
import { ModalContagens } from './ModalContagens'

export function OS({ inventory }) {
  const [os, setOs] = useState([])
  const [isLoadingOs, setIsLoadingOs] = useState(true)
  const [orderIds, setOrderIds] = useState([])
  const [numeroContagens, setNumeroContagens] = useState([])
  const [modalContagens, setModalContagens] = useState({
    isOpen: false,
    os: null,
  })
  const [search, setSearch] = useState({
    contagem: '',
    deposito: '',
    rua: '',
    lado: '',
    nivel: '',
    tipo: '',
    os: '',
  })

  const [isModalFuncionariosOpen, setIsModalFuncionariosOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [isAssignAccountantLoading, setIsAssignAccountantLoading] =
    useState(false)

  const filteredOs = useMemo(() => {
    let data = os

    if (search.deposito) {
      data = data.filter((o) =>
        String(o.descricao).toUpperCase().includes(`DEP${search.deposito}`),
      )
    }

    if (search.rua) {
      data = data.filter((o) =>
        String(o.descricao).toUpperCase().includes(`RUA${search.rua}`),
      )
    }

    if (search.lado) {
      data = data.filter(
        (o) => String(o.descricao.split('-')[2]).toUpperCase() === search.lado,
      )
    }

    if (search.nivel) {
      data = data.filter((o) =>
        String(o.descricao).toUpperCase().includes(`NIVEL${search.nivel}`),
      )
    }

    if (search.tipo) {
      data = data.filter((o) =>
        String(o.descricao).toUpperCase().includes(search.tipo),
      )
    }

    if (search.os) {
      data = data.filter((o) => Number(o.id) === Number(search.os))
    }

    if (search.contagem) {
      data = data.filter((o) => Number(o.contagem) === Number(search.contagem))
    }

    return data
  }, [
    os,
    search.deposito,
    search.rua,
    search.lado,
    search.nivel,
    search.tipo,
    search.os,
    search.contagem,
  ])

  const { filteredItems, Th, Paginate, itemsPerPage, setItemsPerPage } =
    useOrdenation(filteredOs, 'id', 'desc', 100)

  function handleChangeSearch(e) {
    const { name, value } = e.target

    setSearch((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  async function handleAssignAccountant(funcionario) {
    try {
      setIsAssignAccountantLoading(true)

      const promises = []

      for (const id of selectedIds) {
        promises.push(
          WmsInventarioOSService.assignAccountant(id, {
            contador: funcionario.IDFUNCIONARIO,
          }),
        )
      }

      await Promise.all(promises)

      await loadOs()

      if (selectedIds.length > 1) {
        toast.success('Ordens atribuídas com sucesso!')
      } else {
        toast.success('Ordem atribuída com sucesso!')
      }
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao atribuir contador')
    } finally {
      setIsAssignAccountantLoading(false)
    }
  }

  function selectAllIds(data) {
    for (const order of data) {
      setSelectedIds((prevState) => {
        if (!prevState.includes(order.id)) {
          return [...prevState, order.id]
        }

        return prevState
      })
    }
  }

  function removeAllIds(data) {
    for (const order of data) {
      setSelectedIds((prevState) => {
        return prevState.filter((orderId) => orderId !== order.id)
      })
    }
  }

  useEffect(() => {}, [])

  const loadOs = useCallback(async () => {
    try {
      setIsLoadingOs(true)

      const data = await WmsInventarioOSService.getAll({
        inventoryId: inventory.info.id,
      })

      for (const order of data) {
        setOrderIds((prevState) => {
          if (!prevState.includes(order.id)) {
            return [...prevState, order.id]
          }

          return prevState
        })

        setNumeroContagens((prevState) => {
          if (!prevState.includes(order.contagem)) {
            return [...prevState, order.contagem]
          }

          return prevState
        })
      }

      setSelectedIds([])
      setOs(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível carregar as ordens de serviço')
    } finally {
      setIsLoadingOs(false)
    }
  }, [inventory.info.id])

  useEffect(() => {
    if (inventory.isDivisionConfirmed) {
      loadOs()
    }
  }, [inventory.isDivisionConfirmed, loadOs])

  return (
    <>
      <ComponentContainer>
        <ComponentContent>
          <p>
            Atribua as ordens de serviços aos seus contadores, inicie e/ou
            finalize as contagens.
          </p>

          <Ghost isVisible={!inventory.isDivisionConfirmed}>
            <Alert variant="warning">
              Crie a divisão (quebra) dos endereços para continuar.
            </Alert>
          </Ghost>

          <Ghost isVisible={inventory.isDivisionConfirmed}>
            <Row>
              <Col>
                <Form.Label>Depósito</Form.Label>
                <Form.Control
                  name="deposito"
                  value={search.deposito}
                  onChange={handleChangeSearch}
                />
              </Col>

              <Col>
                <Form.Label>Rua</Form.Label>
                <Form.Control
                  name="rua"
                  value={search.rua}
                  onChange={handleChangeSearch}
                />
              </Col>

              <Col>
                <Form.Label>Lado</Form.Label>
                <Form.Select
                  name="lado"
                  value={search.lado}
                  onChange={handleChangeSearch}
                >
                  <option value="">Todos</option>
                  <option value="PAR">Par</option>
                  <option value="IMPAR">Ímpar</option>
                </Form.Select>
              </Col>

              <Col>
                <Form.Label>Nível</Form.Label>
                <Form.Control
                  name="nivel"
                  value={search.nivel}
                  onChange={handleChangeSearch}
                />
              </Col>

              <Col>
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  name="tipo"
                  value={search.tipo}
                  onChange={handleChangeSearch}
                >
                  <option value="">Todos</option>
                  <option value="AP">AP</option>
                  <option value="AE">AE</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col>
                <Form.Label>Nº OS</Form.Label>
                <Form.Select
                  name="os"
                  value={search.os}
                  onChange={handleChangeSearch}
                >
                  <option value="">Todas</option>
                  {orderIds.map((orderId) => (
                    <option value={orderId}>{orderId}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col>
                <Form.Label>Contagem</Form.Label>
                <Form.Select
                  name="contagem"
                  value={search.contagem}
                  onChange={handleChangeSearch}
                >
                  <option value="">Todas</option>
                  {numeroContagens.map((contagem) => (
                    <option value={contagem}>{contagem}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col>
                <Form.Label>OS p/ página</Form.Label>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <div
                  style={{
                    display: 'flex',
                    gap: 5,
                  }}
                >
                  <CustomButton
                    title="Atualizar"
                    onClick={loadOs}
                    noMargin
                    isDisabled={isLoadingOs}
                  >
                    <ArrowsClockwise />
                  </CustomButton>

                  <CustomButton
                    noMargin
                    title="Atr. Cont."
                    onClick={() => setIsModalFuncionariosOpen(true)}
                    isDisabled={selectedIds.length === 0 || isLoadingOs}
                    isLoading={isAssignAccountantLoading}
                    isLoadingText="Atribuindo..."
                  >
                    <PersonFillAdd />
                  </CustomButton>
                </div>
              </Col>
            </Row>

            <LoaderWMS isLoading={isLoadingOs}>
              {filteredItems.length > 0 ? (
                <>
                  <Table
                    hover
                    responsive
                    bordered
                  >
                    <thead>
                      <tr>
                        <th colSpan="11">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <CustomButton
                              noMargin
                              title="Selecionar/Desmarcar Todos"
                              onClick={() => {
                                if (selectedIds.length > 0) {
                                  setSelectedIds([])
                                } else {
                                  selectAllIds(filteredOs)
                                }
                              }}
                            >
                              <Check2Square />
                            </CustomButton>

                            <p
                              style={{
                                all: 'unset',
                              }}
                            >
                              {selectedIds.length} selecionados
                            </p>

                            <p
                              style={{
                                all: 'unset',
                              }}
                            >
                              Mostrando {filteredItems.length} de{' '}
                              {filteredOs.length}
                            </p>
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th>
                          <Form.Check
                            checked={
                              selectedIds.length > 0 &&
                              filteredItems.every((order) =>
                                selectedIds.includes(order.id),
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllIds(filteredItems)
                              } else {
                                removeAllIds(filteredItems)
                              }
                            }}
                          />
                        </th>

                        <Th
                          label="ID"
                          value="id"
                        />
                        <Th
                          label="Desc."
                          value="descricao"
                        />
                        <Th
                          label="Contador"
                          value="contador"
                        />
                        <Th
                          label="Contagem"
                          value="contagem"
                        />
                        <Th
                          label="Total End."
                          value="total_enderecos"
                        />
                        <Th
                          label="End. Contados"
                          value="enderecos_contados"
                        />
                        <Th
                          label="Status"
                          value="status"
                        />
                        <Th
                          label="In."
                          value="data_inicio"
                        />
                        <Th
                          label="Fim"
                          value="data_fim"
                        />
                        <Th label="Ação" />
                      </tr>
                    </thead>

                    <tbody>
                      {filteredItems.map((order) => (
                        <tr>
                          <td>
                            <Form.Check
                              checked={selectedIds.includes(order.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedIds((prevState) => [
                                    ...prevState,
                                    order.id,
                                  ])
                                } else {
                                  setSelectedIds((prevState) =>
                                    prevState.filter(
                                      (orderId) => orderId !== order.id,
                                    ),
                                  )
                                }
                              }}
                            />
                          </td>
                          <td>{order.id}</td>
                          <td>{order.descricao}</td>
                          <td>{getValue(order.contador)}</td>
                          <td>{getValue(order.contagem)}</td>
                          <td>{getValue(order.total_enderecos)}</td>
                          <td>{getValue(order.enderecos_contados)}</td>
                          <td>
                            <OverlayTrigger
                              overlay={
                                <Tooltip>
                                  {order.status.trim() == 'A'
                                    ? 'Aberta'
                                    : order.status.trim() == 'I'
                                      ? 'Iniciada'
                                      : order.status.trim() == 'C'
                                        ? 'Cancelada'
                                        : 'Finalizada'}
                                </Tooltip>
                              }
                            >
                              {order.status.trim() == 'A' ? (
                                <Hourglass
                                  color="#f39c12"
                                  size={20}
                                />
                              ) : order.status.trim() == 'I' ? (
                                <HourglassSplit
                                  color="#3498db"
                                  size={20}
                                />
                              ) : order.status.trim() == 'C' ? (
                                <XCircle
                                  color="#D10000"
                                  size={20}
                                  weight="fill"
                                />
                              ) : (
                                <HourglassBottom
                                  color="#2A9134"
                                  size={20}
                                />
                              )}
                            </OverlayTrigger>
                          </td>
                          <td>{getValue(order.data_inicio_contagem)}</td>
                          <td>{getValue(order.data_fim_contagem)}</td>
                          <td>
                            <OverlayTrigger
                              overlay={<Tooltip>Visualizar Contagens</Tooltip>}
                            >
                              <CustomButton
                                onClick={() =>
                                  setModalContagens({
                                    isOpen: true,
                                    os: order,
                                  })
                                }
                              >
                                <Eye weight="fill" />
                              </CustomButton>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <Paginate />
                </>
              ) : (
                <>
                  <p>Sem dados disponíveis</p>
                </>
              )}
            </LoaderWMS>
          </Ghost>
        </ComponentContent>
      </ComponentContainer>

      {modalContagens.isOpen && (
        <ModalContagens
          isOpen={modalContagens.isOpen}
          onClose={() =>
            setModalContagens({
              isOpen: false,
              os: null,
            })
          }
          os={modalContagens.os}
        />
      )}

      {isModalFuncionariosOpen && (
        <ModalFuncionarios
          isOpen={isModalFuncionariosOpen}
          onClose={() => setIsModalFuncionariosOpen(false)}
          onSelectFuncionario={handleAssignAccountant}
        />
      )}
    </>
  )
}
